// Quote Builder — Jigsaw (TKC normalised catalogue) pickers.
//
// These mount INSIDE the left-hand Products panel (not the right-hand picker
// area). The whole experience — cascade + cabinet list, or accessory list —
// lives on the left; the right pane stays quote-only. Two mounts:
//
//   • mountJigsawCabinetPicker — the cabinets cascade, stacked vertically for
//       the narrow LH panel: Door Range → Door Colour → Cabinet (carcase)
//       Colour, then a scrollable cabinet list with dynamic product-group
//       filter chips above it (All + only the groups present, with counts).
//       Plana colours group by colour_family in the Door Colour <optgroup>s.
//       Door colour "Painted" (tier=pto) opens the existing PTO custom-colour
//       selector (37 colours, longer lead). "Painted Signature Select"
//       (tier=signature) expands into the supplier's fixed signature_colours
//       list at STOCK price — a plain selectable list, NOT a PTO trigger.
//
//   • mountJigsawAccessoryPicker — Handles / Sinks / Taps / Lighting /
//       Accessories go straight to a product list.
//
// Reads catalogue_* via /api/catalogue/jigsaw/* (migration 0030). Adds lines
// through the shared pushItemToZone + recalcItem so pricing, discounts and
// totals behave identically to every other picker.

import { api } from "@tkos/api-client";
import type { ItemCategory, QuoteItem } from "./types";
import type { QuoteStore } from "./state";
import { escapeHtml, fmtGbp, ICONS, newId, svgIcon } from "./formatters";
import { recalcItem } from "./pricing";
import { pushItemToZone } from "./simple-pickers";
import { loadDiscountsFor, resolveDiscPct } from "./discounts";

// ── Constants ────────────────────────────────────────────────────────────
const SUPPLIER_KEY = "TKC";    // item.supplier label + discount cache key (both brands are TKC)
/** On TKC's discount sheet Jigsaw is ONE row ("JIGSAW 50%"), for every Jigsaw
 *  line whatever its range (Steve, 2026-10-01). */
const JIGSAW_SHEET = { names: ["Jigsaw"], sheetNamesOnly: true };

/** Per-collection config. `showCarcase` is false for white-only collections
 *  (Modus) — the single carcase is auto-selected and its dropdown hidden. */
export interface CollectionConfig {
  brand: string;        // 'Jigsaw' | 'Modus'
  supplierSlug: string; // resolves to suppliers.id on the server (both 'tkc')
  showCarcase: boolean;
}
const DEFAULT_CFG: CollectionConfig = { brand: "Jigsaw", supplierSlug: "tkc", showCarcase: true };

/** Canonical product-group order + labels for the dynamic filter chips. */
const GROUP_ORDER = ["base", "wall", "tall", "drawer", "appliance", "corner", "panel", "other"] as const;
const GROUP_LABEL: Record<string, string> = {
  base: "Base", wall: "Wall", tall: "Tall", drawer: "Drawer",
  appliance: "Appliance", corner: "Corner", panel: "Panel", other: "Other",
};

// ── Cabinet list ordering (2026-05-31) ────────────────────────────────────
// The /catalogue/jigsaw/cabinets endpoint returns rows ordered by
// product_group (alphabetical), width, description — so the LH list was in no
// useful order. We re-sort client-side into a three-level order Steve
// approved: category → product family → width ascending.
//
//   1. CATEGORY — the canonical GROUP_ORDER above (Base → Wall → Tall →
//      Drawer → Appliance → Corner → Panel → Other), not alphabetical.
//   2. FAMILY — derived from keywords in the description so like units
//      cluster: Standard (no keyword) first, then Dovetail, Pull-out, Blum,
//      Elite, Sink/Belfast, Curved/Radius last. Evaluated in this order;
//      first keyword hit wins, so an item matching two families lands in the
//      earlier one (e.g. an "ELITE PULL OUT" sorts under Pull-out).
//   3. WIDTH ascending (nulls last), then description as a stable tie-break.
const GROUP_RANK = new Map<string, number>(GROUP_ORDER.map((g, i) => [g, i]));
function cabGroupRank(group: string | null): number {
  return GROUP_RANK.get(group ?? "other") ?? GROUP_ORDER.length;
}

const CAB_FAMILY_RULES: ReadonlyArray<{ rank: number; re: RegExp }> = [
  { rank: 1, re: /DOVETAIL/ },
  { rank: 2, re: /PULL[\s-]?OUT/ },
  { rank: 3, re: /BLUM/ },
  { rank: 4, re: /ELITE/ },
  { rank: 5, re: /SINK|BELFAST/ },
  { rank: 6, re: /CURVED|RADIUS/ },
];
function cabFamilyRank(description: string | null): number {
  const d = (description ?? "").toUpperCase();
  for (const { rank, re } of CAB_FAMILY_RULES) {
    if (re.test(d)) return rank;
  }
  return 0; // standard — no specialist keyword
}

/** Order cabinets by category → family → width asc → description. Pure;
 *  returns a new array. */
function sortCabinets(list: JCabinet[]): JCabinet[] {
  return list.slice().sort((a, b) => {
    const g = cabGroupRank(a.productGroup) - cabGroupRank(b.productGroup);
    if (g !== 0) return g;
    const f = cabFamilyRank(a.description) - cabFamilyRank(b.description);
    if (f !== 0) return f;
    const aw = a.w ?? Number.POSITIVE_INFINITY;
    const bw = b.w ?? Number.POSITIVE_INFINITY;
    if (aw !== bw) return aw - bw;
    return (a.description ?? a.code).localeCompare(b.description ?? b.code);
  });
}

export interface JigsawPickerHandle {
  unmount(): void;
}

// ── Wire shapes (/api/catalogue/jigsaw/*) ──────────────────────────────────
interface JRange { id: string; name: string; finishType: string | null; }
type JTier = "stock" | "pto" | "signature";
interface JColour { id: string; colourName: string; colourFamily: string | null; tier: JTier; }
interface JCarcase { id: string; code: string; name: string; }
interface JCabinet {
  sku: string; code: string; description: string | null; productGroup: string | null;
  h: number | null; w: number | null; d: number | null; price: number | null;
}
interface JAccessory {
  sku: string; description: string | null; price: number | null;
  h: number | null; w: number | null; d: number | null;
}

async function fetchList<T>(path: string, key: string): Promise<T[]> {
  try {
    const data = await api<Record<string, unknown>>(path);
    const arr = data[key];
    return Array.isArray(arr) ? (arr as T[]) : [];
  } catch (err) {
    console.warn("[qb-jigsaw] fetch failed", path, err);
    return [];
  }
}

function zoneOpts(store: QuoteStore, selId: string): string {
  return store.get().zones.map(
    (z) => `<option value="${escapeHtml(z.id)}"${z.id === selId ? " selected" : ""}>${escapeHtml(z.name)}</option>`,
  ).join("");
}

/** Escape a value for use inside a CSS attribute selector ([data-x='…']). */
function cssEscape(v: string): string {
  return v.replace(/['\\]/g, "\\$&");
}

// ════════════════════════════════════════════════════════════════════════
// Cabinet cascade picker (left-hand panel)
// ════════════════════════════════════════════════════════════════════════

interface CabState {
  zoneId: string;
  qty: number;
  ranges: JRange[];
  rangesLoading: boolean;
  rangeId: string;
  colours: JColour[];
  coloursLoading: boolean;
  colourId: string;
  /** Chosen PTO or Signature colour name (only when the door colour tier is
   *  pto/signature). For stock colours the door colour IS the chosen colour. */
  paintColour: string;
  ptoColours: string[];
  sigColours: string[];
  carcases: JCarcase[];
  carcaseId: string;
  cabinets: JCabinet[];
  cabinetsLoading: boolean;
  /** Product-group filter chip: "all" or a product_group value. */
  groupFilter: string;
}

export function mountJigsawCabinetPicker(
  host: HTMLElement,
  store: QuoteStore,
  cfg: CollectionConfig = DEFAULT_CFG,
): JigsawPickerHandle {
  const s: CabState = {
    zoneId: store.get().zones[0]?.id ?? "",
    qty: 1,
    ranges: [], rangesLoading: true, rangeId: "",
    colours: [], coloursLoading: false, colourId: "",
    paintColour: "", ptoColours: [], sigColours: [],
    carcases: [], carcaseId: "",
    cabinets: [], cabinetsLoading: false,
    groupFilter: "all",
  };

  void loadDiscountsFor(SUPPLIER_KEY);

  // ── Derived helpers ──
  const selectedColour = (): JColour | undefined => s.colours.find((c) => c.id === s.colourId);
  const selectedRange = (): JRange | undefined => s.ranges.find((r) => r.id === s.rangeId);
  const selectedCarcase = (): JCarcase | undefined => s.carcases.find((c) => c.id === s.carcaseId);

  function effectiveColour(): string {
    const c = selectedColour();
    if (!c) return "";
    return c.tier === "stock" ? c.colourName : s.paintColour;
  }
  function colourResolved(): boolean {
    const c = selectedColour();
    if (!c) return false;
    return c.tier === "stock" ? true : s.paintColour !== "";
  }
  function filteredCabinets(): JCabinet[] {
    if (s.groupFilter === "all") return s.cabinets;
    return s.cabinets.filter((c) => (c.productGroup ?? "other") === s.groupFilter);
  }

  // ── Loaders ──
  async function loadRanges(): Promise<void> {
    s.ranges = await fetchList<JRange>(`/catalogue/jigsaw/door-ranges?supplier=${cfg.supplierSlug}&brand=${cfg.brand}`, "ranges");
    s.rangesLoading = false;
    paint();
  }
  async function loadCarcases(): Promise<void> {
    s.carcases = await fetchList<JCarcase>(`/catalogue/jigsaw/carcase-colours?supplier=${cfg.supplierSlug}&brand=${cfg.brand}`, "carcases");
    // White-only collections (showCarcase=false): auto-select the single
    // carcase and load cabinets if the door colour is already resolved.
    if (!cfg.showCarcase && s.carcases.length && !s.carcaseId) {
      s.carcaseId = s.carcases[0]!.id;
      if (colourResolved()) void loadCabinets();
    }
    paint();
  }
  async function loadColours(rangeId: string): Promise<void> {
    s.coloursLoading = true;
    paint();
    const colours = await fetchList<JColour>(`/catalogue/jigsaw/door-colours?range=${rangeId}`, "colours");
    if (s.rangeId !== rangeId) return; // superseded
    s.colours = colours;
    s.coloursLoading = false;
    paint();
  }
  async function loadPtoColours(): Promise<void> {
    if (s.ptoColours.length) return;
    s.ptoColours = await fetchList<string>(`/catalogue/pto-colours?supplier=${cfg.supplierSlug}`, "colours");
    paint();
  }
  async function loadSigColours(): Promise<void> {
    if (s.sigColours.length) return;
    s.sigColours = await fetchList<string>(`/catalogue/signature-colours?supplier=${cfg.supplierSlug}`, "colours");
    paint();
  }
  async function loadCabinets(): Promise<void> {
    if (!s.colourId || !s.carcaseId) { s.cabinets = []; return; }
    s.cabinetsLoading = true;
    s.groupFilter = "all";
    paint();
    const colourId = s.colourId, carcaseId = s.carcaseId;
    const cabinets = await fetchList<JCabinet>(
      `/catalogue/jigsaw/cabinets?doorColour=${colourId}&carcase=${carcaseId}`, "cabinets",
    );
    if (s.colourId !== colourId || s.carcaseId !== carcaseId) return; // superseded
    s.cabinets = sortCabinets(cabinets);
    s.cabinetsLoading = false;
    paint();
  }

  void loadRanges();
  void loadCarcases();

  // ── Add to quote ──
  function addCabinet(cab: JCabinet): void {
    const range = selectedRange();
    const carcase = selectedCarcase();
    const colour = selectedColour();
    if (!range || !carcase || !colour || !colourResolved()) return;
    const doorColour = effectiveColour();
    const discPct = resolveDiscPct(SUPPLIER_KEY, null, range.name, JIGSAW_SHEET) ?? 0;
    const item: QuoteItem = {
      id: newId("i"),
      category: "cabinets",
      supplier: SUPPLIER_KEY,
      code: cab.sku,
      desc: `${range.name} · ${doorColour} · ${cab.description ?? cab.code}`,
      qty: s.qty > 0 ? s.qty : 1,
      width: cab.w ?? 0,
      height: cab.h ?? 0,
      depth: cab.d ?? 0,
      notes: "",
      listPrice: cab.price ?? 0,
      discPct,
      tradePrice: 0,
      markupMode: "pct",
      markupPct: store.get().defaults.markupPct ?? 0,
      markupGbp: 0,
      retailPrice: 0,
      doorRange: range.name,
      doorColour,
      carcaseColour: carcase.name,
      doorType: colour.tier,
    };
    recalcItem(item, null);
    pushItemToZone(store, s.zoneId, item);
    flashAdded(cab.sku);
  }

  function flashAdded(sku: string): void {
    const card = host.querySelector<HTMLElement>(`[data-cab='${cssEscape(sku)}']`);
    if (!card) return;
    card.classList.add("is-added");
    window.setTimeout(() => card.classList.remove("is-added"), 700);
  }

  // ── Render ──
  function render(): string {
    return `
      <div class="qb-door-picker__inner">
        <div class="qb-door-picker__head">
          <span class="qb-door-picker__title">Jigsaw cabinets</span>
          <span class="qb-door-picker__hint">Range → colour → cabinet colour</span>
        </div>

        <div class="qb-jigsaw-form">
          <div class="qb-jigsaw-row2">
            <div>
              <label class="qb-door-label">Add to zone</label>
              <select class="qb-door-input" data-role="zone">${zoneOpts(store, s.zoneId)}</select>
            </div>
            <div>
              <label class="qb-door-label">Qty</label>
              <input type="number" min="1" class="qb-door-input" data-role="qty" value="${s.qty}" />
            </div>
          </div>
          <div>
            <label class="qb-door-label">Door range<span class="qb-door-req"> *</span></label>
            <select class="qb-door-input" data-role="range">${rangeOpts()}</select>
          </div>
          <div>
            <label class="qb-door-label">Door colour<span class="qb-door-req"> *</span></label>
            ${colourSelect()}
          </div>
          ${paintBlock()}
          ${cfg.showCarcase ? `
          <div>
            <label class="qb-door-label">Cabinet colour<span class="qb-door-req"> *</span></label>
            <select class="qb-door-input" data-role="carcase"${s.rangeId ? "" : " disabled"}>${carcaseOpts()}</select>
          </div>` : ""}
        </div>

        ${cabinetSection()}
      </div>
    `;
  }

  function rangeOpts(): string {
    if (s.rangesLoading) return `<option value="">Loading…</option>`;
    const opts = s.ranges.map(
      (r) => `<option value="${escapeHtml(r.id)}"${r.id === s.rangeId ? " selected" : ""}>${escapeHtml(r.name)}</option>`,
    ).join("");
    return `<option value="">— Select a range —</option>${opts}`;
  }

  function carcaseOpts(): string {
    if (!s.carcases.length) return `<option value="">Loading…</option>`;
    const opts = s.carcases.map(
      (c) => `<option value="${escapeHtml(c.id)}"${c.id === s.carcaseId ? " selected" : ""}>${escapeHtml(c.name)}</option>`,
    ).join("");
    return `<option value="">— Select a colour —</option>${opts}`;
  }

  function colourSelect(): string {
    if (!s.rangeId) return `<select class="qb-door-input" data-role="colour" disabled><option value="">Pick a range first</option></select>`;
    if (s.coloursLoading) return `<select class="qb-door-input" data-role="colour" disabled><option value="">Loading…</option></select>`;
    const grouped = s.colours.some((c) => c.colourFamily);
    let body = `<option value="">— Select a colour —</option>`;
    if (grouped) {
      const families = new Map<string, JColour[]>();
      const loose: JColour[] = [];
      for (const c of s.colours) {
        if (c.colourFamily) {
          const arr = families.get(c.colourFamily) ?? [];
          arr.push(c);
          families.set(c.colourFamily, arr);
        } else loose.push(c);
      }
      for (const [fam, list] of families) {
        body += `<optgroup label="${escapeHtml(fam)}">${list.map(colourOpt).join("")}</optgroup>`;
      }
      if (loose.length) body += `<optgroup label="Other">${loose.map(colourOpt).join("")}</optgroup>`;
    } else {
      body += s.colours.map(colourOpt).join("");
    }
    return `<select class="qb-door-input" data-role="colour">${body}</select>`;
  }

  function colourOpt(c: JColour): string {
    const tag = c.tier === "pto" ? " (Paint to Order)" : c.tier === "signature" ? " (Signature Select)" : "";
    return `<option value="${escapeHtml(c.id)}"${c.id === s.colourId ? " selected" : ""}>${escapeHtml(c.colourName)}${tag}</option>`;
  }

  /** PTO / Signature sub-selector shown when the chosen door colour needs one. */
  function paintBlock(): string {
    const c = selectedColour();
    if (!c || c.tier === "stock") return "";
    if (c.tier === "pto") {
      const palette = s.ptoColours;
      const body = palette.length
        ? `<select class="qb-door-input" data-role="paint-colour">
             <option value="">— Select a PTO colour —</option>
             ${palette.map((p) => `<option value="${escapeHtml(p)}"${p === s.paintColour ? " selected" : ""}>${escapeHtml(p)}</option>`).join("")}
           </select>`
        : `<div class="qb-door-empty">Loading PTO colours…</div>`;
      return `
        <div>
          <div class="qb-door-label-row">
            <label class="qb-door-label">Paint to Order colour<span class="qb-door-req"> *</span></label>
            ${palette.length ? `<span class="qb-door-label-hint">${palette.length} · longer lead</span>` : ""}
          </div>
          ${body}
          ${s.paintColour ? `<div class="qb-door-tier qb-door-tier--pto">✓ ${escapeHtml(s.paintColour)} · Paint to order</div>` : ""}
        </div>`;
    }
    // signature — fixed palette at stock price (plain selectable list)
    const palette = s.sigColours;
    const body = palette.length
      ? `<select class="qb-door-input" data-role="paint-colour">
           <option value="">— Select a Signature colour —</option>
           ${palette.map((p) => `<option value="${escapeHtml(p)}"${p === s.paintColour ? " selected" : ""}>${escapeHtml(p)}</option>`).join("")}
         </select>`
      : `<div class="qb-door-empty">Loading Signature colours…</div>`;
    return `
      <div>
        <div class="qb-door-label-row">
          <label class="qb-door-label">Signature Select colour<span class="qb-door-req"> *</span></label>
          ${palette.length ? `<span class="qb-door-label-hint">${palette.length} · stock price</span>` : ""}
        </div>
        ${body}
      </div>`;
  }

  /** Filter chips + scrollable cabinet list. */
  function cabinetSection(): string {
    if (!s.rangeId || !colourResolved() || !s.carcaseId) {
      const need = cfg.showCarcase ? "range, colour and cabinet colour" : "door style and colour";
      return `<div class="qb-door-empty">Choose a ${need} to see the cabinets.</div>`;
    }
    if (s.cabinetsLoading) return `<div class="qb-door-empty">Loading cabinets…</div>`;
    if (!s.cabinets.length) return `<div class="qb-door-empty">No cabinets for this combination.</div>`;

    // Dynamic filter chips — All + only the groups actually present, with counts.
    const counts = new Map<string, number>();
    for (const cab of s.cabinets) {
      const g = cab.productGroup ?? "other";
      counts.set(g, (counts.get(g) ?? 0) + 1);
    }
    const chips = [`<button type="button" class="qb-chip ${s.groupFilter === "all" ? "is-active" : ""}" data-group="all">All <span class="qb-chip__n">${s.cabinets.length}</span></button>`];
    for (const g of GROUP_ORDER) {
      const n = counts.get(g);
      if (!n) continue;
      chips.push(`<button type="button" class="qb-chip ${s.groupFilter === g ? "is-active" : ""}" data-group="${g}">${escapeHtml(GROUP_LABEL[g] ?? g)} <span class="qb-chip__n">${n}</span></button>`);
    }

    const shown = filteredCabinets();
    const discPct = resolveDiscPct(SUPPLIER_KEY, null, selectedRange()?.name ?? null, JIGSAW_SHEET);
    const rows = shown.map((cab) => {
      // Dimensions render H×W×D (kitchen-industry standard).
      const dims = [cab.h, cab.w, cab.d].some((v) => v)
        ? `<span class="qb-product-hit__dim">${cab.h ?? "-"}×${cab.w ?? "-"}×${cab.d ?? "-"}mm</span>` : "";
      // Handing — TKC handed SKUs end in -L / -R (e.g. R047K15W-L). Surface it
      // as a visible badge so retailers don't have to decode the SKU. Non-handed
      // SKUs get no badge.
      const hand = /-L$/i.test(cab.sku) ? "L" : /-R$/i.test(cab.sku) ? "R" : null;
      const handBadge = hand
        ? `<span class="qb-hand-badge qb-hand-badge--${hand}">${hand === "L" ? "Left" : "Right"}</span>`
        : "";
      const list = cab.price ?? 0;
      const priceHtml = discPct != null && discPct > 0
        ? `<div class="qb-product-hit__price qb-product-hit__price--discounted">
             <div class="qb-product-hit__trade">${fmtGbp(list * (1 - discPct / 100))}</div>
             <div class="qb-product-hit__discnote">${discPct.toFixed(2).replace(/\.?0+$/, "")}% off</div>
           </div>`
        : `<div class="qb-product-hit__price">${list ? fmtGbp(list) : "—"}</div>`;
      return `
        <li class="qb-product-hit" data-cab="${escapeHtml(cab.sku)}" tabindex="0" role="button" aria-label="Add ${escapeHtml(cab.description ?? cab.code)}">
          <div class="qb-product-hit__main">
            <div class="qb-product-hit__desc"><span class="qb-product-hit__name">${escapeHtml(cab.description ?? cab.code)}</span>${handBadge}</div>
            <div class="qb-product-hit__meta"><span class="qb-product-hit__code">${escapeHtml(cab.sku)}</span>${dims}</div>
          </div>
          ${priceHtml}
          <button type="button" class="qb-product-hit__add" data-role="cab-add" title="Add to quote">${svgIcon(ICONS.plus)}</button>
        </li>`;
    }).join("");

    return `
      <div class="qb-jigsaw-filters" role="tablist" aria-label="Cabinet type">${chips.join("")}</div>
      <div class="qb-jigsaw-cabs" data-role="cab-list">
        <ul class="qb-products__results-list">${rows}</ul>
      </div>`;
  }

  function paint(): void {
    host.innerHTML = render();
    wire();
  }

  function wire(): void {
    const $ = <T extends HTMLElement>(role: string): T | null => host.querySelector<T>(`[data-role='${role}']`);

    $<HTMLSelectElement>("zone")?.addEventListener("change", (e) => {
      s.zoneId = (e.target as HTMLSelectElement).value;
    });
    $<HTMLInputElement>("qty")?.addEventListener("input", (e) => {
      const v = Number((e.target as HTMLInputElement).value);
      s.qty = Number.isFinite(v) && v > 0 ? v : 1;
    });
    $<HTMLSelectElement>("range")?.addEventListener("change", (e) => {
      s.rangeId = (e.target as HTMLSelectElement).value;
      s.colourId = ""; s.paintColour = ""; s.colours = []; s.cabinets = []; s.groupFilter = "all";
      paint();
      if (s.rangeId) void loadColours(s.rangeId);
    });
    $<HTMLSelectElement>("colour")?.addEventListener("change", (e) => {
      s.colourId = (e.target as HTMLSelectElement).value;
      s.paintColour = "";
      paint();
      const c = selectedColour();
      if (c?.tier === "pto") void loadPtoColours();
      else if (c?.tier === "signature") void loadSigColours();
      void loadCabinets();
    });
    $<HTMLSelectElement>("paint-colour")?.addEventListener("change", (e) => {
      s.paintColour = (e.target as HTMLSelectElement).value;
      paint();
    });
    $<HTMLSelectElement>("carcase")?.addEventListener("change", (e) => {
      s.carcaseId = (e.target as HTMLSelectElement).value;
      paint();
      void loadCabinets();
    });

    // Filter chips — client-side, no fetch.
    for (const chip of host.querySelectorAll<HTMLButtonElement>("[data-group]")) {
      chip.addEventListener("click", () => {
        s.groupFilter = chip.dataset["group"] ?? "all";
        paint();
      });
    }

    for (const li of host.querySelectorAll<HTMLElement>("li.qb-product-hit")) {
      const sku = li.dataset["cab"];
      if (!sku) continue;
      const cab = s.cabinets.find((c) => c.sku === sku);
      if (!cab) continue;
      const add = (): void => addCabinet(cab);
      li.querySelector<HTMLButtonElement>("[data-role='cab-add']")?.addEventListener("click", (e) => {
        e.stopPropagation();
        add();
      });
      li.addEventListener("click", add);
      li.addEventListener("keydown", (e) => {
        const ke = e as KeyboardEvent;
        if (ke.key === "Enter" || ke.key === " ") { ke.preventDefault(); add(); }
      });
    }
  }

  paint();
  return { unmount(): void { host.innerHTML = ""; } };
}

// ════════════════════════════════════════════════════════════════════════
// Accessory list pickers (Handles / Sinks / Taps / Lighting / Accessories)
// ════════════════════════════════════════════════════════════════════════

const ACCESSORY_MAP: Record<string, { group: string; category: ItemCategory; label: string }> = {
  Handles:     { group: "Handles",     category: "handles",  label: "Handles" },
  Sinks:       { group: "Sinks",       category: "sundries", label: "Sinks" },
  Taps:        { group: "Taps",        category: "sundries", label: "Taps" },
  Lighting:    { group: "Lighting",    category: "lighting", label: "Lighting" },
  Accessories: { group: "Accessories", category: "sundries", label: "Accessories" },
};

export function mountJigsawAccessoryPicker(
  host: HTMLElement,
  store: QuoteStore,
  subCat: string,
  coll: CollectionConfig = DEFAULT_CFG,
): JigsawPickerHandle {
  const cfg = ACCESSORY_MAP[subCat] ?? ACCESSORY_MAP["Accessories"]!;
  const s = {
    zoneId: store.get().zones[0]?.id ?? "",
    qty: 1,
    items: [] as JAccessory[],
    loading: true,
  };

  void loadDiscountsFor(SUPPLIER_KEY);

  async function load(): Promise<void> {
    s.items = await fetchList<JAccessory>(
      `/catalogue/jigsaw/accessories?supplier=${coll.supplierSlug}&brand=${coll.brand}&group=${encodeURIComponent(cfg.group)}`,
      "accessories",
    );
    s.loading = false;
    paint();
  }

  function addAccessory(acc: JAccessory): void {
    const item: QuoteItem = {
      id: newId("i"),
      category: cfg.category,
      supplier: SUPPLIER_KEY,
      code: acc.sku,
      desc: acc.description ?? acc.sku,
      qty: s.qty > 0 ? s.qty : 1,
      width: acc.w ?? 0,
      height: acc.h ?? 0,
      depth: acc.d ?? 0,
      notes: "",
      listPrice: acc.price ?? 0,
      discPct: resolveDiscPct(SUPPLIER_KEY, null, null, JIGSAW_SHEET) ?? 0,
      tradePrice: 0,
      markupMode: "pct",
      markupPct: store.get().defaults.markupPct ?? 0,
      markupGbp: 0,
      retailPrice: 0,
    };
    recalcItem(item, null);
    pushItemToZone(store, s.zoneId, item);
    const card = host.querySelector<HTMLElement>(`[data-acc='${cssEscape(acc.sku)}']`);
    if (card) { card.classList.add("is-added"); window.setTimeout(() => card.classList.remove("is-added"), 700); }
  }

  function render(): string {
    let list: string;
    if (s.loading) list = `<div class="qb-door-empty">Loading ${escapeHtml(cfg.label.toLowerCase())}…</div>`;
    else if (!s.items.length) list = `<div class="qb-door-empty">No ${escapeHtml(cfg.label.toLowerCase())} found.</div>`;
    else {
      const rows = s.items.map((acc) => {
        // Dimensions render H×W×D (kitchen-industry standard).
        const dims = [acc.h, acc.w, acc.d].some((v) => v)
          ? `<span class="qb-product-hit__dim">${acc.h ?? "-"}×${acc.w ?? "-"}×${acc.d ?? "-"}mm</span>` : "";
        return `
          <li class="qb-product-hit" data-acc="${escapeHtml(acc.sku)}" tabindex="0" role="button" aria-label="Add ${escapeHtml(acc.description ?? acc.sku)}">
            <div class="qb-product-hit__main">
              <div class="qb-product-hit__desc">${escapeHtml(acc.description ?? acc.sku)}</div>
              <div class="qb-product-hit__meta"><span class="qb-product-hit__code">${escapeHtml(acc.sku)}</span>${dims}</div>
            </div>
            <div class="qb-product-hit__price">${acc.price != null ? fmtGbp(acc.price) : "—"}</div>
            <button type="button" class="qb-product-hit__add" data-role="acc-add" title="Add to quote">${svgIcon(ICONS.plus)}</button>
          </li>`;
      }).join("");
      list = `<div class="qb-jigsaw-cabs"><ul class="qb-products__results-list">${rows}</ul></div>`;
    }
    return `
      <div class="qb-door-picker__inner">
        <div class="qb-door-picker__head">
          <span class="qb-door-picker__title">Jigsaw ${escapeHtml(cfg.label)}</span>
          <span class="qb-door-picker__hint">Click an item to add it</span>
        </div>
        <div class="qb-jigsaw-form">
          <div class="qb-jigsaw-row2">
            <div>
              <label class="qb-door-label">Add to zone</label>
              <select class="qb-door-input" data-role="zone">${zoneOpts(store, s.zoneId)}</select>
            </div>
            <div>
              <label class="qb-door-label">Qty</label>
              <input type="number" min="1" class="qb-door-input" data-role="qty" value="${s.qty}" />
            </div>
          </div>
        </div>
        ${list}
      </div>
    `;
  }

  function paint(): void {
    host.innerHTML = render();
    host.querySelector<HTMLSelectElement>("[data-role='zone']")?.addEventListener("change", (e) => {
      s.zoneId = (e.target as HTMLSelectElement).value;
    });
    host.querySelector<HTMLInputElement>("[data-role='qty']")?.addEventListener("input", (e) => {
      const v = Number((e.target as HTMLInputElement).value);
      s.qty = Number.isFinite(v) && v > 0 ? v : 1;
    });
    for (const li of host.querySelectorAll<HTMLElement>("li.qb-product-hit")) {
      const sku = li.dataset["acc"];
      if (!sku) continue;
      const acc = s.items.find((a) => a.sku === sku);
      if (!acc) continue;
      const add = (): void => addAccessory(acc);
      li.querySelector<HTMLButtonElement>("[data-role='acc-add']")?.addEventListener("click", (e) => { e.stopPropagation(); add(); });
      li.addEventListener("click", add);
      li.addEventListener("keydown", (e) => {
        const ke = e as KeyboardEvent;
        if (ke.key === "Enter" || ke.key === " ") { ke.preventDefault(); add(); }
      });
    }
  }

  paint();
  void load();
  return { unmount(): void { host.innerHTML = ""; } };
}
